import useSnackbar from "@/helpers/add-snackbar"
import { Button } from "@material-ui/core"
import { KeyboardArrowDown, KeyboardArrowRight } from "@material-ui/icons"
import React, { useState } from "react"
import JsonCopyButtons from "../buttons/utility/JsonCopyButtons"
import { useStyles } from "./JsonViewerWithCopy"

interface JsonNodeProps {
  data: any
  path: string[]
  onCopyValue: (value: any) => void
  onCopyPath: (path: string[]) => void
  onCopyFormatted: (path: string[], value: any) => void
  onCopyFormattedMultiArray: (path: string[], value: any) => void
  collapsed: boolean
  parentData?: any
  renderValue?: (value: any) => React.ReactNode
  searchQuery?: string
  lineCounter?: { count: number } // optional, for recursion
}
const JsonNode: React.FC<JsonNodeProps> = ({
  data,
  path,
  onCopyValue,
  onCopyPath,
  onCopyFormatted,
  onCopyFormattedMultiArray,
  collapsed: globalCollapsed,
  parentData,
  renderValue,
  searchQuery = "",
  lineCounter = { count: 0 }
}) => {
  const [isCollapsed, setIsCollapsed] = useState(globalCollapsed)
  const classes = useStyles()
  const { addSnackbar } = useSnackbar()

  const isOdd = lineCounter.count % 2 === 1

  React.useEffect(() => {
    setIsCollapsed(globalCollapsed)
  }, [globalCollapsed])

  const matchesSearch = (value: any): boolean => {
    if (typeof value === "string") {
      return value.toLowerCase().includes(searchQuery.toLowerCase())
    } else if (typeof value === "number" || typeof value === "boolean") {
      return String(value).toLowerCase().includes(searchQuery.toLowerCase())
    } else if (value === null) {
      return false
    } else if (Array.isArray(value)) {
      return value.some(item => matchesSearch(item))
    } else if (typeof value === "object") {
      return Object.values(value).some(v => matchesSearch(v))
    }
    return false
  }

  const filterEntries = (entries: [string, any][]) => {
    return entries.filter(([key, value]) => {
      const keyMatches = key.toLowerCase().includes(searchQuery.toLowerCase())
      const valueMatches = matchesSearch(value)
      return keyMatches || valueMatches
    })
  }

  const filterArray = (arr: any[]) => {
    return arr.filter(item => matchesSearch(item))
  }

  if (data === null) {
    return (
      <span
        className={
          classes.jsonGroup + " " + (isOdd ? classes.altBackground : "")
        }
      >
        <span className={classes.nullValue}>null</span>
        <JsonCopyButtons
          onCopyValue={() => onCopyValue(data)}
          onCopyPath={() => onCopyPath(path)}
          onCopyFormatted={() => onCopyFormatted(path, parentData || data)}
          onCopyFormattedMultiArray={() =>
            onCopyFormattedMultiArray(path, parentData || data)
          }
          classes={classes}
        />
      </span>
    )
  }

  if (typeof data === "boolean") {
    return (
      <span
        className={
          classes.jsonGroup + " " + (isOdd ? classes.altBackground : "")
        }
      >
        <span className={classes.booleanValue}>{data.toString()}</span>
        <JsonCopyButtons
          onCopyValue={() => onCopyValue(data)}
          onCopyPath={() => onCopyPath(path)}
          onCopyFormatted={() => onCopyFormatted(path, parentData || data)}
          onCopyFormattedMultiArray={() =>
            onCopyFormattedMultiArray(path, parentData || data)
          }
          classes={classes}
        />
      </span>
    )
  }

  if (typeof data === "number") {
    return (
      <span
        className={
          classes.jsonGroup + " " + (isOdd ? classes.altBackground : "")
        }
      >
        <span className={classes.numberValue}>{data}</span>
        <JsonCopyButtons
          onCopyValue={() => onCopyValue(data)}
          onCopyPath={() => onCopyPath(path)}
          onCopyFormatted={() => onCopyFormatted(path, parentData || data)}
          onCopyFormattedMultiArray={() =>
            onCopyFormattedMultiArray(path, parentData || data)
          }
          classes={classes}
        />
      </span>
    )
  }

  if (typeof data === "string") {
    let parsed = data
    if (data.startsWith("{") && data.endsWith("}")) {
      try {
        parsed = JSON.parse(data)
        return (
          <span className={classes.jsonGroup}>
            <JsonNode
              data={parsed}
              path={path}
              onCopyValue={onCopyValue}
              onCopyPath={onCopyPath}
              onCopyFormatted={onCopyFormatted}
              onCopyFormattedMultiArray={onCopyFormattedMultiArray}
              collapsed={globalCollapsed}
              lineCounter={{ count: lineCounter.count + 1 }}
            />
          </span>
        )
      } catch (error) {
        addSnackbar(error, "error")
      }
    }

    return (
      <span
        className={
          classes.jsonGroup + " " + (isOdd ? classes.altBackground : "")
        }
      >
        <span className={classes.stringValue}>
          {renderValue ? renderValue(data) : data}
        </span>
        <JsonCopyButtons
          onCopyValue={() => onCopyValue(data)}
          onCopyPath={() => onCopyPath(path)}
          onCopyFormatted={() => onCopyFormatted(path, parentData || data)}
          onCopyFormattedMultiArray={() =>
            onCopyFormattedMultiArray(path, parentData || data)
          }
          classes={classes}
        />
      </span>
    )
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return (
        <span className={classes.jsonGroup}>
          <span>[]</span>
          <JsonCopyButtons
            onCopyValue={() => onCopyValue(data)}
            onCopyPath={() => onCopyPath(path)}
            onCopyFormatted={() => onCopyFormatted(path, data)}
            onCopyFormattedMultiArray={() =>
              onCopyFormattedMultiArray(path, data)
            }
            classes={classes}
          />
        </span>
      )
    }
    const filteredData = searchQuery ? filterArray(data) : data

    return (
      <div className={classes.treeContainer}>
        <span className={classes.jsonGroup}>
          <span
            className={classes.collapsible}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <KeyboardArrowRight fontSize="small" />
            ) : (
              <KeyboardArrowDown fontSize="small" />
            )}
            <span className={classes.punctuation}>[{filteredData.length}]</span>
          </span>
          <JsonCopyButtons
            onCopyValue={() => onCopyValue(data)}
            onCopyPath={() => onCopyPath(path)}
            onCopyFormatted={() => onCopyFormatted(path, data)}
            onCopyFormattedMultiArray={() =>
              onCopyFormattedMultiArray(path, data)
            }
            classes={classes}
          />
        </span>
        {!isCollapsed && (
          <div className={classes.nestedContainer}>
            {filteredData.map((item, index) => (
              <div
                key={index}
                className={`${classes.arrayItem} ${
                  index % 2 === 0 ? classes.altIndexBackground : ""
                }`}
              >
                <span className={`${classes.punctuation} ${classes.shrink}`}>
                  {index}:{" "}
                </span>
                <span className={classes.inlineBlock}>
                  <JsonNode
                    data={item}
                    path={[
                      ...path,
                      "[i=" +
                        index.toString() +
                        "t=" +
                        filteredData.length.toString() +
                        "]"
                    ]}
                    onCopyValue={onCopyValue}
                    onCopyPath={onCopyPath}
                    onCopyFormatted={onCopyFormatted}
                    onCopyFormattedMultiArray={onCopyFormattedMultiArray}
                    collapsed={globalCollapsed}
                    parentData={item}
                    renderValue={renderValue}
                    searchQuery={searchQuery}
                    lineCounter={{ count: lineCounter.count + index }}
                  />
                  {index < filteredData.length - 1 && (
                    <span className={classes.punctuation}>,</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (typeof data === "object") {
    const entries = Object.entries(data)
    if (entries.length === 0) {
      return (
        <span className={classes.jsonGroup}>
          <span>{"{}"}</span>
          <JsonCopyButtons
            onCopyValue={() => onCopyValue(data)}
            onCopyPath={() => onCopyPath(path)}
            onCopyFormatted={() => onCopyFormatted(path, data)}
            onCopyFormattedMultiArray={() =>
              onCopyFormattedMultiArray(path, data)
            }
            classes={classes}
          />
        </span>
      )
    }
    const filteredEntries = searchQuery ? filterEntries(entries) : entries
    // Helper to render a single line with background

    return (
      <div className={classes.treeContainer}>
        <span className={classes.jsonGroup}>
          <span
            className={classes.collapsible}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <KeyboardArrowRight fontSize="small" />
            ) : (
              <KeyboardArrowDown fontSize="small" />
            )}
            <span className={classes.punctuation}>
              {"{"}
              {filteredEntries.length}
              {"}"}
            </span>
          </span>
          <JsonCopyButtons
            onCopyValue={() => onCopyValue(data)}
            onCopyPath={() => onCopyPath(path)}
            onCopyFormatted={() => onCopyFormatted(path, data)}
            onCopyFormattedMultiArray={() =>
              onCopyFormattedMultiArray(path, parentData || data)
            }
            classes={classes}
          />
        </span>
        {!isCollapsed && (
          <div className={classes.nestedContainer}>
            {filteredEntries.map(([key, value], index) => (
              <div
                key={key + "_filteredEntries_" + index}
                className={classes.arrayItem}
              >
                <span
                  className={`${classes.keyName} ${classes.shrink} ${
                    index % 2 === 0 ? classes.altKeyBackground : ""
                  }`}
                >
                  {renderValue ? renderValue(key) : key}
                  {key === "dataset_id" && (
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      size="small"
                      style={{ marginLeft: "5px" }}
                      onClick={() =>
                        window.open(
                          `/admin/datasetcomparisontool/${value}`,
                          "_blank"
                        )
                      }
                    >
                      View Dataset
                    </Button>
                  )}
                  {key === "upload" && (
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      size="small"
                      style={{ marginLeft: "5px" }}
                      onClick={() =>
                        window.open(
                          `/admin/datasetlookupbyuploadid/${value?.id}`,
                          "_blank"
                        )
                      }
                    >
                      View Datasets
                    </Button>
                  )}
                </span>
                <span
                  className={`${classes.punctuation} ${classes.shrink} ${
                    index % 2 === 0 ? classes.altKeyBackground : ""
                  }`}
                >
                  :
                </span>
                <span className={classes.inlineBlock}>
                  <JsonNode
                    data={value}
                    path={[...path, key]}
                    onCopyValue={onCopyValue}
                    onCopyPath={onCopyPath}
                    onCopyFormatted={onCopyFormatted}
                    onCopyFormattedMultiArray={onCopyFormattedMultiArray}
                    collapsed={globalCollapsed}
                    renderValue={renderValue}
                    searchQuery={searchQuery}
                    lineCounter={{
                      count: lineCounter.count + (isOdd ? 0 : 1) + index
                    }} // Ensures even lines
                  />
                  {index < filteredEntries.length - 1 && (
                    <span className={classes.punctuation}>,</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return <span>{String(data)}</span>
}
export default JsonNode
